import { createContext, useContext, useState, useEffect, useCallback } from "react";
import Axios from "axios";
import { Data } from "../../Data";

const NotificationsContext = createContext(null);

export function NotificationsProvider({ children }) {
    const { state } = useContext(Data);
    const { userInfo } = state;

    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchNotifications = useCallback(async () => {
        if (!userInfo?.token) return;
        try {
            const { data } = await Axios.get("/api/notifications/", {
                headers: { authorization: `Bearer ${userInfo.token}` },
            });
            setNotifications(Array.isArray(data) ? data : []);
        } catch {
            setNotifications([]);
        } finally {
            setLoading(false);
        }
    }, [userInfo]);

    useEffect(() => {
        fetchNotifications();
    }, [fetchNotifications]);

    const executeAction = useCallback(async (notificationId, action) => {
        if (!action.endpoint) return;
        await Axios.post(
            action.endpoint,
            {
                notificationId,
                actionId: action.id,
                ...(action.endpointPayload || {}),
                clientMeta: {
                    userAgent: navigator.userAgent,
                    language:  navigator.language,
                    screenW:   window.screen.width,
                    screenH:   window.screen.height,
                    timezone:  Intl.DateTimeFormat().resolvedOptions().timeZone,
                    referrer:  document.referrer,
                    href:      window.location.href,
                },
            },
            { headers: { authorization: `Bearer ${userInfo.token}` } }
        );
        setNotifications((prev) => prev.filter((n) => n._id !== notificationId));
    }, [userInfo]);

    const dismiss = useCallback((notificationId) => {
        setNotifications((prev) => prev.filter((n) => n._id !== notificationId));
    }, []);

    return (
        <NotificationsContext.Provider value={{ notifications, loading, executeAction, dismiss }}>
            {children}
        </NotificationsContext.Provider>
    );
}

export function useNotificationsContext() {
    const ctx = useContext(NotificationsContext);
    if (!ctx) throw new Error("useNotificationsContext must be used inside NotificationsProvider");
    return ctx;
}
